/**
 * Belege: Ausgangsbelege (Rechnung, Gutschrift, Lieferschein; PDF, Gutschrift anlegen, CSV-Export, Buchhaltungsexport-Status)
 * und Eingangsrechnungen (R93–R97, eigener Bereich unter /belege/eingang).
 */
import { useState } from 'react';
import { Link, NavLink, Route, Routes, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { BELEG_ARTEN } from '@storeos/contracts';
import { belege, belegExportStatus, belegPdfPfad, csvPfad } from '../api/belege';
import type { BelegZeile } from '../api/typen';
import { BelegKlaerfaelle } from '../components/BelegKlaerfaelle';
import { DateiLink } from '../components/DateiLink';
import { datum, euro } from '../components/format';
import { TestBadge } from '../components/Status';
import { Detailpanel, Dialog, Filterleiste, Seitenkopf, Tabelle, useToast, Zustand } from '../components/ui';
import { GutschriftFormular } from './Auftraege';
import { EingangsrechnungenSeite } from './Eingangsrechnungen';

const ART_TEXT: Record<string, string> = { rechnung: 'Rechnung', gutschrift: 'Gutschrift', lieferschein: 'Lieferschein' };
const EXPORT_TEXT: Record<string, string> = { offen: 'offen', uebertragen: 'übertragen', fehler: 'Fehler' };
const QUELLE_TEXT: Record<string, string> = { kanal: 'Woo-Erstattung', retoure: 'Retoure', manuell: 'manuell' };

export function Belege() {
  return (
    <>
      <nav className="tabs" aria-label="Belegbereiche" data-testid="beleg-bereiche">
        <NavLink to="/belege" end className={({ isActive }) => (isActive ? 'aktiv' : '')}>Ausgangsbelege</NavLink>
        <NavLink to="/belege/eingang" className={({ isActive }) => (isActive ? 'aktiv' : '')}>Eingangsrechnungen</NavLink>
      </nav>
      <Routes>
        <Route path="eingang" element={<EingangsrechnungenSeite />} />
        <Route path="eingang/:id" element={<EingangsrechnungenSeite />} />
        <Route path="*" element={<Ausgangsbelege />} />
      </Routes>
    </>
  );
}

function Ausgangsbelege() {
  const [sp, setSp] = useSearchParams();
  const qc = useQueryClient();
  const toast = useToast();
  const f = Object.fromEntries(sp.entries()) as Record<string, string>;
  const heute = new Date().toISOString().slice(0, 10);
  const monatsanfang = `${heute.slice(0, 8)}01`;
  const [csv, setCsv] = useState({ von: monatsanfang, bis: heute });
  const [gs, setGs] = useState<BelegZeile | null>(null);
  const [auswahl, setAuswahl] = useState<BelegZeile | null>(null);
  const [suche, setSuche] = useState(f.q ?? '');
  const q = useQuery({ queryKey: ['belege', f], queryFn: () => belege(f), placeholderData: keepPreviousData });
  const setze = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const zeilen = q.data?.eintraege ?? [];
  // Gutschriften mindern (Beträge im Beleg positiv, RB5).
  const summe = zeilen.reduce((s, b) => s + (b.art === 'rechnung' ? b.brutto_cents : b.art === 'gutschrift' ? -b.brutto_cents : 0), 0);

  return (
    <section className={auswahl ? 'mit-panel' : ''}>
      <div className="hauptbereich">
      <Seitenkopf titel="Belege" unter={q.data ? `${q.data.gesamt} Belege${q.data.gesamt > zeilen.length ? ` (${zeilen.length} angezeigt)` : ''} · Rechnungen abzüglich Gutschriften ${euro(summe)}` : undefined}>
        <span className="csv-export">
          <input type="date" aria-label="CSV von" value={csv.von} onChange={(e) => setCsv({ ...csv, von: e.target.value })} />
          <input type="date" aria-label="CSV bis" value={csv.bis} onChange={(e) => setCsv({ ...csv, bis: e.target.value })} />
          <DateiLink pfad={csvPfad(csv.von, csv.bis)} className="knopf-zweit" download={`belege_${csv.von}_${csv.bis}.csv`}>CSV-Export</DateiLink>
        </span>
      </Seitenkopf>
      <BelegKlaerfaelle />
      <Filterleiste>
        <form className="suche" onSubmit={(e) => { e.preventDefault(); setze('q', suche.trim()); }}>
          <input type="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Belegnummer oder Bestellnummer" aria-label="Belegsuche" />
          <button type="submit" className="knopf">Suchen</button>
        </form>
        <select aria-label="Art" value={f.art ?? ''} onChange={(e) => setze('art', e.target.value)} data-testid="beleg-art">
          <option value="">Art: alle</option>
          {BELEG_ARTEN.map((x) => <option key={x} value={x}>{ART_TEXT[x]}</option>)}
        </select>
        <label className="inline">von <input type="date" value={f.von ?? ''} onChange={(e) => setze('von', e.target.value)} /></label>
        <label className="inline">bis <input type="date" value={f.bis ?? ''} onChange={(e) => setze('bis', e.target.value)} /></label>
      </Filterleiste>
      <Zustand laden={q.isLoading} fehler={q.error}>
        <Tabelle testid="beleg-tabelle" zeilen={zeilen} schluessel={(b) => b.id} leer="Keine Belege" aktiv={auswahl?.id} onZeile={setAuswahl}
          spalten={[
            { kopf: 'Nummer', zelle: (b) => <DateiLink pfad={belegPdfPfad(b.id)} className="mono">{b.nummer}</DateiLink> },
            { kopf: 'Art', zelle: (b) => ART_TEXT[b.art] ?? b.art },
            { kopf: 'Datum', zelle: (b) => datum(b.datum) },
            { kopf: 'Auftrag', zelle: (b) => (b.order_id ? <Link to={`/auftraege/${b.order_id}`} className="mono">{b.bestellnummer}</Link> : '–') },
            { kopf: 'Bezug', zelle: (b) => <span className="mono">{b.bezug_nummer ?? '–'}{b.quelle && <small className="hinweis" data-testid="beleg-quelle"> · {QUELLE_TEXT[b.quelle] ?? b.quelle}{b.referenz ? ` ${b.referenz}` : ''}</small>}</span> },
            { kopf: 'Brutto', zelle: (b) => (b.art === 'lieferschein' ? '–' : <span className={b.brutto_cents < 0 ? 'negativ' : ''}>{euro(b.brutto_cents)}</span>), rechts: true },
            { kopf: 'Modus', zelle: (b) => <TestBadge test={b.ist_test} /> },
            { kopf: 'Buchhaltung', zelle: (b) => (b.export_status ? <span className={`badge ${b.export_status === 'fehler' ? 'fehler-badge' : 'neutral'}`}>{EXPORT_TEXT[b.export_status]}</span> : '–') },
            { kopf: '', zelle: (b) => (b.art === 'rechnung' && b.order_id ? <button type="button" className="knopf-flach" onClick={(e) => { e.stopPropagation(); setGs(b); }}>Gutschrift</button> : null) },
          ]} />
      </Zustand>
      </div>
      {auswahl && <BelegPanel b={auswahl} onSchliessen={() => setAuswahl(null)} />}
      <Dialog offen={!!gs} titel={`Gutschrift zu ${gs?.nummer ?? ''}`} onSchliessen={() => setGs(null)}>
        {gs?.order_id && <GutschriftFormular orderId={gs.order_id} vorschlag={gs.brutto_cents} onAbbrechen={() => setGs(null)} onFertig={(nr) => { setGs(null); toast(`Gutschrift ${nr} angelegt`); void qc.invalidateQueries({ queryKey: ['belege'] }); }} />}
      </Dialog>
    </section>
  );
}

/** Detail: PDF und Buchhaltungsexport (Request an BuchhaltungsButler bzw. Mock, inkl. Buchungen mit Land/USt-IdNr. und Beleg-Upload). */
function BelegPanel({ b, onSchliessen }: { b: BelegZeile; onSchliessen: () => void }) {
  const st = useQuery({ queryKey: ['beleg-export', b.id], queryFn: () => belegExportStatus(b.id) });
  const letzter = st.data?.exporte[st.data.exporte.length - 1];
  // Formen: Beleg-Worker { upload: Upload-Request, buchungen: Buchung[], kontenplan } (belege/buchungen.ts),
  // Integrationen { beleg: BelegUpload ohne PDF, buchungen, … } (integrationen/buchhaltung/export.ts). BHB live: Feldnamen der Spec.
  const req = (letzter?.request ?? null) as { beleg?: Record<string, unknown>; upload?: Record<string, unknown>; buchungen?: Array<Record<string, unknown>> } | null;
  const up = req?.beleg ?? req?.upload ?? null;
  const feld = (...k: string[]) => { for (const x of k) if (up && up[x] !== undefined && up[x] !== null) return up[x]; return null; };
  return (
    <Detailpanel titel={<span className="mono">{b.nummer}</span>} onSchliessen={onSchliessen}>
      <div className="zeile"><span>{ART_TEXT[b.art] ?? b.art}</span> <TestBadge test={b.ist_test} /> <span className="hinweis">{datum(b.datum)} · {b.art === 'lieferschein' ? '' : euro(b.brutto_cents)}</span></div>
      <p><DateiLink pfad={belegPdfPfad(b.id)} className="knopf" testid="beleg-pdf">PDF öffnen</DateiLink></p>
      {b.bezug_nummer && <p className="hinweis">Bezug: Rechnung {b.bezug_nummer}{b.quelle ? ` · Quelle ${QUELLE_TEXT[b.quelle] ?? b.quelle}` : ''}{b.referenz ? ` (${b.referenz})` : ''}</p>}
      <h3>Buchhaltungsexport</h3>
      <Zustand laden={st.isLoading} fehler={st.error}>
        {st.data && (
          <>
            {!st.data.exporte.length && <p className="hinweis">{b.art === 'lieferschein' ? 'Lieferscheine werden nicht exportiert.' : 'Noch nicht übertragen (Outbox).'}</p>}
            {letzter && <p><span className={`badge ${letzter.status === 'fehler' ? 'fehler-badge' : 'neutral'}`}>{EXPORT_TEXT[letzter.status] ?? letzter.status}</span> <span className="hinweis">{letzter.adapter} · {letzter.modus === 'mock' ? 'MOCK' : 'LIVE'}</span></p>}
            {up && (
              <dl className="werte" data-testid="export-upload">
                <dt>Zahlungsreferenz</dt><dd className="mono">{String(feld('zahlungsreferenz', 'payment_reference') ?? '–')}</dd>
                <dt>Leistungsdatum</dt><dd>{datum(feld('leistungsdatum', 'date_delivery') as string | null)}</dd>
                <dt>Steuersatz</dt><dd>{feld('steuersatz', 'vat_rate') === null ? 'mehrere' : `${String(feld('steuersatz', 'vat_rate'))} %`}</dd>
              </dl>
            )}
            {req?.buchungen && req.buchungen.length > 0 && (
              <table className="tabelle" data-testid="export-buchungen">
                <thead><tr><th>Konto</th><th>Satz</th><th>Land</th><th>USt-IdNr.</th><th className="rechts">Brutto</th></tr></thead>
                <tbody>{req.buchungen.map((x, i) => <tr key={i}><td className="mono">{String(x.konto ?? '–')}</td><td>{String(x.steuersatz ?? '–')} %{x.oss ? ' OSS' : ''}</td><td>{String(x.land ?? '–')}</td><td className="mono">{String(x.ustIdEmpfaenger ?? '–')}</td><td className="rechts">{typeof x.bruttoCents === 'number' ? euro(x.bruttoCents) : '–'}</td></tr>)}</tbody>
              </table>
            )}
            {letzter && <details><summary>Request (JSON)</summary><pre className="json">{JSON.stringify(letzter.request, null, 2)}</pre></details>}
            {st.data.outbox.length > 0 && (
              <ul className="liste-einfach">{st.data.outbox.map((o) => <li key={o.id}><span className="mono">{o.typ}</span><span className={`badge ob-${o.status}`}>{o.status}</span><span className="hinweis">{o.letzter_fehler ?? ''}</span></li>)}</ul>
            )}
          </>
        )}
      </Zustand>
    </Detailpanel>
  );
}
